// src/pages/CartPage.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './CartPage.css';

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, cartTotal, clearCart, itemCount } = useCart();
  const navigate = useNavigate();
  
  // DEFENSIVE: Ensure we always work with an array
  const cartItems = Array.isArray(cart) ? cart : [];
  
  const subtotal = cartTotal || 0;
  const tax = subtotal * 0.00;
  const deliveryFee = subtotal >= 250 ? 0 : 20.00;
  const total = subtotal + tax + deliveryFee;

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }
    navigate('/checkout');
  };

  const getStrainColor = (strain) => {
    if (!strain) return 'cart-strain-default';
    switch(strain) {
      case 'Sativa': return 'cart-strain-sativa';
      case 'Indica': return 'cart-strain-indica';
      case 'Hybrid': return 'cart-strain-hybrid';
      default: return 'cart-strain-default';
    }
  };

  return (
    <div className="cart-page">
      <div className="cart-container">
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-content">
            <div>
              <h1>Shopping Cart</h1>
              <p className="cart-subtitle">Review your items before checkout</p>
            </div>
            {cartItems.length > 0 && (
              <button
                onClick={clearCart}
                className="cart-clear-btn"
              >
                <svg className="clear-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear All
              </button>
            )}
          </div>
        </div>

        {cartItems.length === 0 ? (
          // Empty State
          <div className="cart-empty">
            <div className="empty-icon">
              <span>🛒</span>
            </div>
            <h2>Your cart is empty</h2>
            <p className="empty-text">Looks like you haven't added any products yet</p>
            <div className="empty-actions">
              <Link
                to="/products"
                className="empty-btn primary"
              >
                Browse Products
              </Link>
              <Link
                to="/categories"
                className="empty-btn secondary"
              >
                View Categories
              </Link>
            </div>
          </div>
        ) : (
          <div className="cart-content">
            {/* Cart Items Table */}
            <div className="cart-items-section">
              <div className="cart-table">
                {/* Table Header */}
                <div className="cart-table-header">
                  <div className="cart-col-product">Product</div>
                  <div className="cart-col-price">Price</div>
                  <div className="cart-col-quantity">Qty</div>
                  <div className="cart-col-total">Total</div>
                  <div className="cart-col-action"></div>
                </div>

                {/* Cart Items */}
                <div className="cart-items-list">
                  {cartItems.map(item => {
                    // Add safety checks for each item
                    if (!item) return null;
                    
                    const itemKey = item.key || 'unknown';
                    const itemName = item.displayName || item.name || 'Unknown Product';
                    const itemPrice = item.price || 0;
                    const itemQuantity = item.quantity || 1;
                    const itemStrain = item.strain || 'Unknown';
                    
                    return (
                      <div key={itemKey} className="cart-item-row">
                        {/* Product Column */}
                        <div className="cart-col-product">
                          <div className="cart-item-info">
                            <div className="cart-item-image">
                              <img 
                                src={item.image || 'https://images.unsplash.com/photo-1601164606564-dc66c6f7dd2c?w=400&h=400&fit=crop'} 
                                alt={itemName}
                                loading="lazy"
                              />
                            </div>
                            <div className="cart-item-details">
                              <h3 className="cart-item-title">
                                {itemName}
                              </h3>
                              <div className="cart-item-tags">
                                <span className={`cart-tag ${getStrainColor(itemStrain)}`}>
                                  {itemStrain}
                                </span>
                                {item.selectedOption && (
                                  <span className="cart-tag option">
                                    {item.selectedOption.option}
                                  </span>
                                )}
                              </div>
                              <div className="cart-item-sku">
                                SKU: {item.sku || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Price Column */}
                        <div className="cart-col-price">
                          <div className="cart-price-info">
                            <div className="cart-price">${itemPrice.toFixed(2)}</div>
                            <div className="cart-price-label">each</div>
                          </div>
                        </div>

                        {/* Quantity Column */}
                        <div className="cart-col-quantity">
                          <div className="cart-quantity-controls">
                            <button
                              onClick={() => updateQuantity(itemKey, -1)}
                              disabled={itemQuantity <= 1}
                              className="cart-qty-btn"
                            >
                              <span>−</span>
                            </button>
                            <span className="cart-qty-value">{itemQuantity}</span>
                            <button
                              onClick={() => updateQuantity(itemKey, 1)}
                              className="cart-qty-btn"
                            >
                              <span>+</span>
                            </button>
                          </div>
                        </div>

                        {/* Total Column */}
                        <div className="cart-col-total">
                          <div className="cart-total-price">
                            ${(itemPrice * itemQuantity).toFixed(2)}
                          </div>
                        </div>

                        {/* Remove Column */}
                        <div className="cart-col-action">
                          <button
                            onClick={() => removeFromCart(itemKey)}
                            className="cart-remove-btn"
                            title="Remove item"
                          >
                            <svg className="remove-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="cart-summary-section">
              <div className="cart-summary">
                <div className="summary-header">
                  <h2>Order Summary</h2>
                  <span className="item-count">
                    {itemCount || 0} item{(itemCount || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Order Details */}
                <div className="summary-details">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Delivery</span>
                    <span className={deliveryFee === 0 ? 'free-delivery' : ''}>
                      {deliveryFee === 0 ? 'FREE' : `$${deliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="summary-divider"></div>
                  <div className="summary-total">
                    <span>Total</span>
                    <div className="total-amount">
                      <div className="total-price">${total.toFixed(2)}</div>
                      <div className="total-currency">USD</div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="summary-actions">
                  <button
                    onClick={handleCheckout}
                    className="checkout-btn"
                  >
                    Proceed to Checkout
                  </button>

                  <Link
                    to="/products"
                    className="continue-btn"
                  >
                    <svg className="back-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Continue Shopping
                  </Link>
                </div>

                {/* Promo Banner */}
                {subtotal < 250 && (
                  <div className="promo-banner">
                    <div className="promo-content">
                      <div className="promo-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="promo-text">
                        <h3>Free Delivery!</h3>
                        <p>Add ${(250 - subtotal).toFixed(2)} more</p>
                        <div className="promo-progress">
                          <div 
                            className="progress-bar"
                            style={{ width: `${Math.min((subtotal / 250) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Information Cards */}
                <div className="info-cards">
                  <div className="info-card payment">
                    <div className="info-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <h3>Free delivery</h3>
                      <p>on all orders 250 and up</p>
                    </div>
                  </div>

                  <div className="info-card age">
                    <div className="info-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <h3>Age 21+ Required</h3>
                      <p>Valid photo ID needed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recommended Products */}
        {cartItems.length > 0 && (
          <div className="recommended-section">
            <h2>Frequently Added Together</h2>
            <div className="recommended-grid">
              <Link
                to="/category/Edibles"
                className="recommended-card"
              >
                <div className="card-icon edibles">
                  <span>🍬</span>
                </div>
                <h3>Edibles</h3>
                <p>THC-infused treats</p>
              </Link>
              
              <Link
                to="/category/Pre-rolls"
                className="recommended-card"
              >
                <div className="card-icon pre-rolls">
                  <span>🚬</span>
                </div>
                <h3>Pre-Rolls</h3>
                <p>Ready to smoke</p>
              </Link>
              
              <Link
                to="/category/cart"
                className="recommended-card"
              >
                <div className="card-icon cartridges">
                  <span>🔋</span>
                </div>
                <h3>Cartridges</h3>
                <p>Discreet & portable</p>
              </Link>
              
              <Link
                to="/category/Concentrates"
                className="recommended-card"
              >
                <div className="card-icon concentrates">
                  <span>⚗️</span>
                </div>
                <h3>Concentrates</h3>
                <p>High-potency extracts</p>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
